import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { AlignLeft, AlignCenter, AlignRight } from 'lucide-react';

export const ResizableImageComponent = (props: any) => {
  const { node, updateAttributes, selected } = props;
  const {
    src,
    alt,
    width,
    textAlign,
    cropTop,
    cropBottom,
    cropLeft,
    cropRight,
  } = node.attrs;

  /**
   * For top/bottom we simply move the image with negative margins.
   * For left/right we simulate a horizontal shift using negative padding on the container
   * so the visible area effectively crops from the sides.
   */
  const boxStyle: React.CSSProperties = {
    width, // width slider still works
  };

  const imgStyle: React.CSSProperties = {
    objectFit: 'cover',

    // enlarge horizontally & vertically to allow cropping on all sides
    width: `100%`,
    height: `auto`,

    // shift left/up to hide the cropped edges
    marginTop: cropTop ? `-${cropTop}px` : undefined,
    marginBottom: cropBottom ? `-${cropBottom}px` : undefined,
  };
  return (
    <NodeViewWrapper
      as="div"
      style={{ display: 'block', textAlign: textAlign || 'left' }}
      className="relative group"
    >
      {/* fixed-width cropping box */}
      <div
        className="overflow-hidden border shadow rounded inline-block w-[748px]" // fixed 748px width
        style={boxStyle}
      >
        <img src={src} alt={alt || ''} style={imgStyle} />
      </div>

      {selected && (
        <div className="image-style-toolbar flex flex-col gap-2 mt-2 p-2 bg-white rounded shadow-md w-[220px]">
          {/* Width of image inside the fixed box */}
          <label className="flex items-center gap-2">
            Width
            <input
              type="range"
              min={100}
              max={748}
              value={parseInt(width)}
              onChange={(e) =>
                updateAttributes({ width: `${e.target.value}px` })
              }
              className="flex-1 cursor-pointer"
            />
          </label>

          {/* Crop sliders */}
          {[
            ['Top', 'cropTop', cropTop],
            ['Bottom', 'cropBottom', cropBottom],
            // ['Left', 'cropLeft', cropLeft],
            // ['Right', 'cropRight', cropRight],
          ].map(([label, key, val]) => (
            <label key={key} className="flex items-center gap-2">
              {label}
              <input
                type="range"
                min={0}
                max={40}
                value={val}
                onChange={(e) =>
                  updateAttributes({
                    [key as string]: parseInt(e.target.value),
                  })
                }
                className="flex-1 cursor-pointer"
              />
            </label>
          ))}

          {/* Alignment */}
          <div className="flex justify-around mt-1">
            <button
              type="button"
              onClick={() => updateAttributes({ textAlign: 'left' })}
              title="Align Left"
            >
              <AlignLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => updateAttributes({ textAlign: 'center' })}
              title="Align Center"
            >
              <AlignCenter size={18} />
            </button>
            <button
              type="button"
              onClick={() => updateAttributes({ textAlign: 'right' })}
              title="Align Right"
            >
              <AlignRight size={18} />
            </button>
          </div>
        </div>
      )}
    </NodeViewWrapper>
  );
};

export const ResizableImage = Node.create({
  name: 'image',
  group: 'block',
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      width: { default: '300px' },
      textAlign: { default: 'left' },
      cropTop: { default: 0 },
      cropBottom: { default: 0 },
      cropLeft: { default: 0 },
      cropRight: { default: 0 },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
        getAttrs: (dom: any) => ({
          src: dom.getAttribute('src'),
          alt: dom.getAttribute('alt'),
          width: dom.style?.width || '300px',
          textAlign: dom.style?.textAlign || 'left',
          cropTop: parseInt(dom.dataset.croptop || '0'),
          cropBottom: parseInt(dom.dataset.cropbottom || '0'),
          cropLeft: parseInt(dom.dataset.cropleft || '0'),
          cropRight: parseInt(dom.dataset.cropright || '0'),
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'img',
      mergeAttributes(HTMLAttributes, {
        'data-croptop': HTMLAttributes.cropTop,
        'data-cropbottom': HTMLAttributes.cropBottom,
        'data-cropleft': HTMLAttributes.cropLeft,
        'data-cropright': HTMLAttributes.cropRight,
        style: `width:${HTMLAttributes.width || '300px'}; text-align:${
          HTMLAttributes.textAlign || 'left'
        };`,
      }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageComponent);
  },
});
